import { Text } from '@frontend/ui-library';
import styles from './PaymentInstructionsDialog.module.css';
import { getInstructionsData } from './PaymentInstructionsDialogData';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogActions,
  Button,
} from '@frontend/ui-library';
import links from '../../constants/links';

export type PaymentInstructionsDialogMethod =
  | 'wallet'
  | 'manual'
  | 'lightning'
  | 'binancePay'
  | 'solanaPayDesktop'
  | 'solanaPayMobile';

type InstructionData = {
  title: string;
  description: string;
  customDescription?: React.ReactNode;
  instructionGroups: {
    title: string;
    icon: React.ReactElement;
    instructions:
      | string[]
      | ((
          currency: string,
          network: string
        ) => (string | React.ReactElement)[]);
  }[];
};

interface PaymentInstructionsDialogProps {
  method: PaymentInstructionsDialogMethod;
  currency: string;
  network: string;
  onRequestClose: () => void;
  isOfframp: boolean;
}

function PaymentInstructionsDialog({
  method,
  currency,
  network,
  onRequestClose,
  isOfframp,
}: PaymentInstructionsDialogProps): JSX.Element {
  const instructionsData = getInstructionsData(isOfframp);
  const data: InstructionData = instructionsData[method];

  const title = data.title;
  const description = data.description;
  const customDescription = data?.customDescription;
  const processedInstructions = data.instructionGroups.map((group) => {
    return {
      title: group.title,
      icon: group.icon,
      instructions:
        typeof group.instructions === 'function'
          ? group.instructions(currency, network)
          : group.instructions,
    };
  });

  return (
    <Dialog
      onRequestClose={onRequestClose}
      data-test-id="payment-instructions-dialog"
    >
      <DialogHeader description={description}>{title}</DialogHeader>
      {customDescription}
      <DialogBody>
        <div className={styles['instruction-cards']}>
          {processedInstructions.map((group, index) => (
            <InstructionCard
              key={index}
              icon={group.icon}
              title={group.title}
              instructions={group.instructions}
            />
          ))}
        </div>
      </DialogBody>
      <DialogActions>
        <Button
          as="a"
          href={links.support.HOW_TO_PAY}
          target="_blank"
          size="medium"
          type="button"
          variant="secondary"
        >
          Learn more
        </Button>
        <Button
          size="medium"
          type="button"
          variant="primary"
          onClick={onRequestClose}
          data-test-id="payment-instructions-dialog-close"
        >
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function InstructionCard({
  icon,
  title,
  instructions,
}: {
  icon: React.ReactNode;
  title: string;
  instructions: React.ReactNode[];
}) {
  return (
    <div className={styles['instruction-card']}>
      <div className={styles['instruction-card-header']}>
        <div className={styles['icon-container']}>{icon}</div>
        <Text
          as="h3"
          size="medium"
          mb="0"
          mt="0"
          color="neutral-900"
          className={styles['title']}
          weight="600"
        >
          {title}
        </Text>
      </div>
      <ul className={styles['instructions-list']}>
        {instructions.map((instruction, index) => (
          <Text as="li" size="small" color="neutral-500" key={index}>
            {instruction}
          </Text>
        ))}
      </ul>
    </div>
  );
}

export default PaymentInstructionsDialog;
